import { useState, useCallback, useMemo } from 'react';
import type { DragStartEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { EditorItem, Question, Section } from './types';

const ELIGIBLE_TRIGGER_TYPES = ['multipleChoice', 'checkbox', 'rating'];

export function flattenQuestions(items: EditorItem[]): Question[] {
  const result: Question[] = [];
  for (const item of items) {
    if (item.kind === 'question') {
      result.push(item.question);
    } else {
      result.push(...item.questions);
    }
  }
  return result;
}

export function toQuestionsAndSections(items: EditorItem[]): {
  questions: Question[];
  sections: Section[];
} {
  const questions: Question[] = [];
  const sections: Section[] = [];
  for (const item of items) {
    if (item.kind === 'question') {
      questions.push({ ...item.question, sectionId: undefined });
    } else {
      sections.push(item.section);
      for (const q of item.questions) {
        questions.push({ ...q, sectionId: item.section.id });
      }
    }
  }
  return { questions, sections };
}

export function buildItems(questions: Question[], sections: Section[]): EditorItem[] {
  const sectionMap = new Map<string, Question[]>();
  const ungrouped: Question[] = [];
  const sectionOrder: string[] = sections.map(s => s.id);

  for (const q of questions) {
    if (q.sectionId && sectionOrder.includes(q.sectionId)) {
      const existing = sectionMap.get(q.sectionId) || [];
      existing.push(q);
      sectionMap.set(q.sectionId, existing);
    } else {
      ungrouped.push(q);
    }
  }

  const items: EditorItem[] = [];

  for (const q of ungrouped) {
    items.push({ kind: 'question', question: q });
  }

  for (const section of sections) {
    items.push({
      kind: 'section',
      section,
      questions: sectionMap.get(section.id) || [],
    });
  }

  return items;
}

function findContainerOfQuestion(
  items: EditorItem[],
  questionId: string
): { itemIndex: number; questionIndex: number; containerId: string } | null {
  for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
    const item = items[itemIdx];
    if (item.kind === 'question' && item.question.id === questionId) {
      return { itemIndex: itemIdx, questionIndex: -1, containerId: 'root' };
    }
    if (item.kind === 'section') {
      const qIdx = item.questions.findIndex(q => q.id === questionId);
      if (qIdx >= 0) {
        return { itemIndex: itemIdx, questionIndex: qIdx, containerId: item.section.id };
      }
    }
  }
  return null;
}

function reindexVisibilityConditions(items: EditorItem[]): EditorItem[] {
  const allQuestions = flattenQuestions(items);

  return items.map(item => {
    if (item.kind === 'question') {
      return {
        ...item,
        question: reindexQuestion(item.question, allQuestions),
      };
    }
    return {
      ...item,
      questions: item.questions.map(q => reindexQuestion(q, allQuestions)),
    };
  });
}

function reindexQuestion(question: Question, allQuestions: Question[]): Question {
  if (!question.visibilityCondition) return question;

  const myIndex = allQuestions.findIndex(q => q.id === question.id);
  const triggerIndex = question.visibilityCondition.triggerQuestionIndex;
  const triggerQuestion = allQuestions[triggerIndex];

  if (!triggerQuestion || myIndex <= triggerIndex) {
    return question;
  }

  if (!ELIGIBLE_TRIGGER_TYPES.includes(triggerQuestion.type)) {
    const { visibilityCondition: _, ...rest } = question;
    return rest;
  }

  return question;
}

export function useEditorDragAndDrop(initialItems: EditorItem[] = []) {
  const [items, setItems] = useState<EditorItem[]>(initialItems);
  const [activeId, setActiveId] = useState<string | null>(null);

  const allQuestions = useMemo(() => flattenQuestions(items), [items]);

  const topLevelIds = useMemo(() => {
    return items.map(item => {
      if (item.kind === 'question') return `q-${item.question.id}`;
      return `s-${item.section.id}`;
    });
  }, [items]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    if (activeIdStr === overIdStr) return;

    if (!activeIdStr.startsWith('q-')) return;

    const activeQId = activeIdStr.slice(2);

    setItems(prev => {
      const sourceInfo = findContainerOfQuestion(prev, activeQId);
      if (!sourceInfo) return prev;

      let targetSectionId: string | null = null;

      if (overIdStr.startsWith('section-drop-')) {
        targetSectionId = overIdStr.slice('section-drop-'.length);
      } else if (overIdStr.startsWith('s-')) {
        targetSectionId = overIdStr.slice(2);
      } else if (overIdStr.startsWith('q-')) {
        const overQId = overIdStr.slice(2);
        const overInfo = findContainerOfQuestion(prev, overQId);
        if (overInfo && overInfo.containerId !== 'root') {
          targetSectionId = overInfo.containerId;
        }
      }

      const targetContainerId = targetSectionId || 'root';
      if (sourceInfo.containerId === targetContainerId) return prev;

      const newItems = prev.map(item => {
        if (item.kind === 'section') {
          return { ...item, questions: [...item.questions] };
        }
        return { ...item };
      });

      let movedQuestion: Question | null = null;

      if (sourceInfo.containerId === 'root') {
        const item = newItems[sourceInfo.itemIndex];
        if (item.kind === 'question') {
          movedQuestion = item.question;
          newItems.splice(sourceInfo.itemIndex, 1);
        }
      } else {
        const sectionItem = newItems[sourceInfo.itemIndex];
        if (sectionItem.kind === 'section') {
          movedQuestion = sectionItem.questions[sourceInfo.questionIndex];
          sectionItem.questions.splice(sourceInfo.questionIndex, 1);
        }
      }

      if (!movedQuestion) return prev;

      if (targetContainerId === 'root') {
        let insertIndex = newItems.length;
        if (overIdStr.startsWith('q-')) {
          const overQId = overIdStr.slice(2);
          const overIdx = newItems.findIndex(
            it => it.kind === 'question' && it.question.id === overQId
          );
          if (overIdx >= 0) insertIndex = overIdx;
        }
        newItems.splice(insertIndex, 0, { kind: 'question', question: movedQuestion });
      } else {
        const sectionIdx = newItems.findIndex(
          it => it.kind === 'section' && it.section.id === targetSectionId
        );
        if (sectionIdx >= 0) {
          const section = newItems[sectionIdx];
          if (section.kind === 'section') {
            if (overIdStr.startsWith('q-')) {
              const overQId = overIdStr.slice(2);
              const qIdx = section.questions.findIndex(q => q.id === overQId);
              if (qIdx >= 0) {
                section.questions.splice(qIdx, 0, movedQuestion);
              } else {
                section.questions.push(movedQuestion);
              }
            } else {
              section.questions.push(movedQuestion);
            }
          }
        }
      }

      return newItems;
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    setItems(prev => {
      if (activeIdStr.startsWith('s-') && overIdStr.startsWith('s-')) {
        const activeIdx = prev.findIndex(
          it => it.kind === 'section' && `s-${it.section.id}` === activeIdStr
        );
        const overIdx = prev.findIndex(
          it => it.kind === 'section' && `s-${it.section.id}` === overIdStr
        );
        if (activeIdx >= 0 && overIdx >= 0 && activeIdx !== overIdx) {
          return reindexVisibilityConditions(arrayMove(prev, activeIdx, overIdx));
        }
      }

      if (activeIdStr.startsWith('q-') && overIdStr.startsWith('q-')) {
        const activeQId = activeIdStr.slice(2);
        const overQId = overIdStr.slice(2);

        const activeInfo = findContainerOfQuestion(prev, activeQId);
        const overInfo = findContainerOfQuestion(prev, overQId);

        if (activeInfo && overInfo && activeInfo.containerId === overInfo.containerId) {
          if (activeInfo.containerId === 'root') {
            const activeIdx = prev.findIndex(
              it => it.kind === 'question' && it.question.id === activeQId
            );
            const overIdx = prev.findIndex(
              it => it.kind === 'question' && it.question.id === overQId
            );
            if (activeIdx >= 0 && overIdx >= 0 && activeIdx !== overIdx) {
              return reindexVisibilityConditions(arrayMove(prev, activeIdx, overIdx));
            }
          } else {
            const sectionIdx = activeInfo.itemIndex;
            const section = prev[sectionIdx];
            if (section.kind === 'section') {
              const activeQIdx = section.questions.findIndex(q => q.id === activeQId);
              const overQIdx = section.questions.findIndex(q => q.id === overQId);
              if (activeQIdx >= 0 && overQIdx >= 0 && activeQIdx !== overQIdx) {
                const newItems = [...prev];
                newItems[sectionIdx] = {
                  ...section,
                  questions: arrayMove(section.questions, activeQIdx, overQIdx),
                };
                return reindexVisibilityConditions(newItems);
              }
            }
          }
        }
      }

      if (activeIdStr.startsWith('q-') && (overIdStr.startsWith('s-') || overIdStr.startsWith('section-drop-'))) {
        return reindexVisibilityConditions(prev);
      }

      return reindexVisibilityConditions(prev);
    });
  }, []);

  const getActiveQuestion = useCallback((): Question | null => {
    if (!activeId || !activeId.startsWith('q-')) return null;
    const qId = activeId.slice(2);
    return allQuestions.find(q => q.id === qId) || null;
  }, [activeId, allQuestions]);

  const getActiveSection = useCallback((): { section: Section; questions: Question[] } | null => {
    if (!activeId || !activeId.startsWith('s-')) return null;
    const sId = activeId.slice(2);
    const item = items.find(it => it.kind === 'section' && it.section.id === sId);
    if (item?.kind === 'section') return { section: item.section, questions: item.questions };
    return null;
  }, [activeId, items]);

  return {
    items,
    setItems,
    activeId,
    topLevelIds,
    allQuestions,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getActiveQuestion,
    getActiveSection,
  };
}
